<template>
  <div class="animated fadeIn">
    <b-row>
      <PageTitle></PageTitle>
      <filter-area
        @filterData="filterData()"
        @resetData="resetFilerAreaData()"
      >
        <b-row>
          <b-col cols="4">
            <b-form-group>
              <label for="status">{{$tc('page.generic.state', 1)}}</label>
              <v-select id="status" v-model="filterArea.status" :options="status" index="code" />
            </b-form-group>
          </b-col>
          <b-col cols="4">
            <b-form-group>
              <label for="sourceCode">{{$t('pendingConsent.sourceCode')}}</label>
              <v-select id="sources" v-model="filterArea.sourceCode" :options="sources" multiple :getOptionLabel="getParsedSources" />
            </b-form-group>
          </b-col>
          <b-col cols="4">
            <b-form-group>
              <label for="patientId">{{$t('pendingConsent.patientId')}}</label>
              <b-form-input
                type="text"
                id="code"
                size="lg"
                :placeholder="$t('pendingConsent.patientId')"
                v-model="filterArea.patientId">
              </b-form-input>
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="6">
            <b-form-group>
              <label for="procedure">{{$tc('filterArea.label.procedure', 1)}}</label>
              <v-select id="procedure" v-model="filterArea.procedureCode" multiple :options="procedures" :getOptionLabel="getParsedProcedures" />
            </b-form-group>
          </b-col>
          <b-col cols="6">
            <b-form-group>
              <label for="models">{{$tc('filterArea.label.model', 1)}}</label>
              <v-select id="models" v-model="filterArea.modelCode" :options="models" multiple :getOptionLabel="getParsedModels" />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col cols="3">
            <b-form-group>
              <label for="dateCreatedFrom">{{$t('filterArea.label.dateCreatedFrom')}}</label>
              <datepicker v-model="filterArea.dateCreatedFrom" name="dateCreatedFrom" input-class="form-control form-control-lg" :language="datepickerLanguage" :format="formatDate"/>
            </b-form-group>
          </b-col>
          <b-col cols="3">
            <b-form-group>
              <label for="expirationDateTo">{{$tc('filterArea.label.dateCreatedTo')}}</label>
              <datepicker v-model="filterArea.dateCreatedTo" name="dateCreatedTo" input-class="form-control form-control-lg" :language="datepickerLanguage" :format="formatDate"/>
            </b-form-group>
          </b-col>
          <b-col cols="3">
            <b-form-group>
              <label for="expirationDateFrom">{{$t('filterArea.label.expirationDateFrom')}}</label>
              <datepicker v-model="filterArea.expirationDateFrom" name="expirationDateFrom" input-class="form-control form-control-lg" :language="datepickerLanguage" :format="formatDate"/>
            </b-form-group>
          </b-col>
          <b-col cols="3">
            <b-form-group>
              <label for="expirationDateTo">{{$tc('filterArea.label.expirationDateTo')}}</label>
              <datepicker v-model="filterArea.expirationDateTo" name="expirationDateTo" input-class="form-control form-control-lg" :language="datepickerLanguage" :format="formatDate"/>
            </b-form-group>
          </b-col>
        </b-row>
      </filter-area>
      <b-col lg="12">
        <b-card>
          <b-table
            :stacked="stacked"
            :hover="hover"
            :striped="striped"
            :bordered="bordered"
            :small="small"
            :fixed="fixed"
            :items="items"
            :fields="captions"
            :current-page="1"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            :sort-direction="sortDirection"
            :per-page="perPage"
            @sort-changed="sortingChanged"
            :no-sort-reset="true"
            :no-local-sorting="true">
            <template slot="status" slot-scope="data">
              {{ getStatus(data.item.status.toLowerCase()) }}
            </template>
            <template slot="procedureList" slot-scope="data">
              {{ data.item.procedureList.length > 0 ? data.item.procedureList[0].code : '' }}
            </template>
            <template slot="dateCreated" slot-scope="data">
              {{ formatDate(data.item.dateCreated) }}
            </template>
            <template slot="expirationDate" slot-scope="data">
              {{ formatDate(data.item.expirationDate) }}
            </template>
            <template slot="actions" slot-scope="data">
              <div class="btn-group" role="group">
                <router-link
                  tag="button"
                  class="btn btn-primary fa fa-search"
                  v-b-tooltip.hover
                  :title="$t('actions.show')"
                  :to="`/pendingConsents/show/${data.item.pendingConsentId}`"
                />
              </div>
            </template>
          </b-table>
          <div class="row d-flex justify-content-between">
            <div class="col-sm-4">
              <b-pagination :total-rows="rowCount" :per-page="perPage" v-model="currentPage"
                            :prev-text="$t('actions.previous')"
                            :next-text="$t('actions.next')"
                            hide-goto-end-buttons @input="fetchData(currentPage)"/>
            </div>
          </div>
        </b-card>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import PageTitle from '../../components/PageTitle'
import FilterArea from "../../components/FilterArea";
import i18n from '@/config/i18n'
import miniToastr from "mini-toastr";
import moment from "moment"
import vSelect from 'vue-select'

import Datepicker from 'vuejs-datepicker';
import {it} from 'vuejs-datepicker/dist/locale'

import PendingConsentService from "@/services/PendingConsentService";
import ProcedureService from "@/services/ProcedureService";
import ModelService from "@/services/ModelService";
import SourceService from "@/services/SourceService";

const pendingConsentService = PendingConsentService.build();
const procedureService = ProcedureService.build();
const sourceService = SourceService.build();
const modelService = ModelService.build();

export default {
  name: "PendingConsentList",
  inheritAttrs: false,
  components: {
    PageTitle,
    vSelect,
    FilterArea,
    Datepicker
  },
  props: {
    caption: {
      type: String,
      default: 'Consensi pendenti'
    },
    stacked: {
      type: String,
      default: 'sm'
    },
    hover: {
      type: Boolean,
      default: true
    },
    striped: {
      type: Boolean,
      default: false
    },
    bordered: {
      type: Boolean,
      default: false
    },
    small: {
      type: Boolean,
      default: false
    },
    fixed: {
      type: Boolean,
      default: false
    },
    perPage: {
      type: Number,
      default: 7
    }
  },
  created() {
    miniToastr.init(); // config can be passed here miniToastr.init(config)
    miniToastr.setIcon('error', 'i', {'class': 'fas fa-exclamation-triangle'});
    this.fetchData(1);
    this.getProcedures();
    this.getModels();
    this.getSources();
  },
  data: () => {
    return {
      fields: [
        {
          key: 'status',
          sortable: true,
          label: i18n.tc('page.generic.state', 1)
        },
        {
          key: 'procedureList',
          sortable: true,
          label: i18n.t('pendingConsent.procedureList'),
        },
        {
          key: 'modelCode',
          sortable: true,
          label: i18n.t('pendingConsent.modelCode'),
        },{
          key: 'patientId',
          sortable: true,
          label: i18n.t('pendingConsent.patientId'),
        },{
          key: 'sourceCode',
          sortable: true,
          label: i18n.t('pendingConsent.sourceCode'),
        },
        {
          key: 'dateCreated',
          sortable: true,
          label: i18n.t('page.generic.creationDate'),
        },
        {
          key: 'expirationDate',
          sortable: false,
          label: i18n.t('page.generic.expirationDate')
        },
        {
          key: 'actions',
          sortable: false,
          label: 'Azioni'
        }
      ],
      currentPage: 1,
      tableData: [],
      tableTotalRows: 0,
      sortBy: 'code',
      sortDesc: false,
      sortDirection: 'asc',
      filter: null,
      status: [
        {
          code: "pending",
          label: i18n.t("pendingConsent.status.pending")
        },
        {
          code: "picked",
          label: i18n.t("pendingConsent.status.picked")
        },
        {
          code: "expired",
          label: i18n.t("pendingConsent.status.expired")
        }
      ],
      procedures: [],
      sources: [],
      models: [],
      datepickerLanguage: it,
      filterArea: {
        status: null,
        sourceCode: null,
        modelCode: null,
        procedureCode: null,
        patientId: null,
        dateCreatedFrom: null,
        dateCreatedTo: null,
        expirationDateFrom: null,
        expirationDateTo: null
      }
    }
  },
  computed: {
    items: function () {
      const items = this.tableData;
      return Array.isArray(items) ? items : []
    },
    captions: function () {
      return this.fields
    },
    rowCount: function () {
      return this.tableTotalRows
    },
    encodedModelFilter: function () {
      return this.filterArea.modelCode.map((model) => model.code);
    },
    encodedSourceFilter: function () {
      return this.filterArea.sourceCode.map((source) => source.code);
    },
    encodedProcedureFilter: function () {
      return this.filterArea.procedureCode.map((procedure) => procedure.code);
    }
  },
  methods: {
    getStatus(status) {
      return i18n.t(`pendingConsent.status.${status}`)
    },
    getParsedModels(model) {
      return `${model.description} (ID: ${model.code})`
    },
    getParsedSources(source) {
      return `${source.label} (ID: ${source.code})`
    },
    getParsedProcedures(procedure) {
      return `${procedure.label} (ID: ${procedure.code})`
    },
    async getProcedures() {
      this.procedures = (await procedureService.search({ max: -1 })).data;
    },
    async getModels() {
      this.models = (await modelService.search({ max: -1 })).data;
    },
    async getSources() {
      this.sources = (await sourceService.search({ max: -1 })).data;
    },
    formatDate (date) {
      if (!date)
        return null

      return moment(date).format('DD/MM/YYYY');
    },
    async fetchData(currentPage) {
      (
        {
          pendingConsents: this.tableData = [],
          total: this.tableTotalRows = 0
        } = await pendingConsentService.search(
          {
            max: this.perPage,
            offset: (currentPage - 1) * this.perPage,
            order: (this.sortDesc ? 'desc' : 'asc'),
            modelCode: this.filterArea.modelCode ? this.encodedModelFilter : null,
            sourceCode: this.filterArea.sourceCode ? this.encodedSourceFilter : null,
            procedureCode: this.filterArea.procedureCode ? this.encodedProcedureFilter : null,
            status: this.filterArea.status,
            patientId: this.filterArea.patientId,
            dateCreatedFrom: this.formatDate(this.filterArea.dateCreatedFrom),
            dateCreatedTo: this.formatDate(this.filterArea.dateCreatedTo),
            expirationDateFrom: this.formatDate(this.filterArea.expirationDateFrom),
            expirationDateTo: this.formatDate(this.filterArea.expirationDateTo)
          }
        )
      )
    },
    sortingChanged(ctx) {
      this.sortBy = ctx.sortBy;
      this.sortDesc = ctx.sortDesc;
      this.fetchData(this.currentPage)
    },
    filterData(){
      this.fetchData(1)
    },
    resetFilerAreaData(){
      this.filterArea = {
        procedureCode: null,
        sourceCode: null,
        modelCode: null,
        status: null,
        patientId: null,
        dateCreatedFrom: null,
        dateCreatedTo: null,
        expirationDateFrom: null,
        expirationDateTo: null
      };
      this.fetchData(1)
    },
  }
}
</script>
